// Card 57, spoil ground (R548, ui-map-interface.md §17.3.57).
//
// A chart rings a spoil ground — the water where the dredgers dump — in a
// dashed circle and writes the words inside it; an old sheet wrote "here
// the ground is worked out". The ring here sits on a deposit whose stock
// has run to zero, and the modern reading gives the hour the field silts
// back up. An empty field is then not a destination, and a reader knows
// when it will be one again.
//
// **THE CLOCK IS ANCHORED, NOT POLLED.** `Site.respawn_sec` is the wire's
// snapshot of a world-second countdown (R548). The piece stamps the wall
// clock when a NEW figure arrives and counts down from that stamp on
// `timescale`, the way the truce and the queue count. A rebuild for the
// minute re-labels the ring; it does not re-anchor it, or the clock would
// stand still. A countdown under an hour reads its seconds (`3m 23s`,
// R2741), so while one stands the piece answers its own edge once a world
// second (`outgrown`).

import { chart as S } from '../../../gen/strings';
import { cellCenter, resourceOf } from '../../../world';
import { SiteKind } from '../../../gen/proto/aquaphobia';
import type { FrameInfo } from '../../layer';
import type { ChartContext, ChartSinks, Piece } from '../context';
import { CHART_INK, dashedRing, faceOf, inkOf } from './shared';
import { leftSpan, leftTicksBySecond } from '../../../locale/span';

/** One worked-out field. */
interface Spoil {
  x: number;
  y: number;
  /** The commodity's own letter (`RESOURCES[i].short`). */
  short: string;
  /** World seconds to the refill at the stamp below, or 0 for no clock. */
  respawnSec: number;
  /** The wall clock when that figure arrived, in ms. */
  atMs: number;
}

export class SpoilGroundPiece implements Piece {
  readonly id = 'spoil_ground';
  readonly tier = 'screen';
  readonly pack = 'static';
  readonly film = 'hide';
  readonly rebuildOn = ['hello', 'sites', 'minute', 'reading', 'font'] as const;

  /** The worked-out fields of this world, in site order. The tests read them. */
  spoils: Spoil[] = [];

  /** A countdown under an hour stands, so the words move each second. */
  private bySecond = false;
  /** The world second of the last build. */
  private second = -1;

  /** The anchored countdown of every field, by site id. */
  private readonly clocks = new Map<number, { sec: number; atMs: number }>();

  rebuild(ctx: ChartContext): void {
    const now = Date.now();
    const sites = [...ctx.state.sites.values()].sort((a, b) => a.id - b.id);
    const live = new Set<number>();
    this.spoils = [];
    this.bySecond = false;
    this.second = Math.floor(ctx.worldClockS);
    for (const s of sites) {
      if (s.kind !== SiteKind.SITE_KIND_DEPOSIT || s.amount !== 0) continue;
      live.add(s.id);
      // A figure that differs from the anchored one is a NEW figure, so it
      // takes a new stamp. The same figure keeps the stamp it came with.
      let clock = this.clocks.get(s.id);
      if (!clock || clock.sec !== s.respawnSec) {
        clock = { sec: s.respawnSec, atMs: now };
        this.clocks.set(s.id, clock);
      }
      const [x, y] = cellCenter(s.cell);
      this.spoils.push({
        x, y,
        short: resourceOf(s.resource)?.short ?? '',
        respawnSec: clock.sec,
        atMs: clock.atMs,
      });
      if (clock.sec > 0 && leftTicksBySecond(leftOf(clock, now, ctx.state.timescale))) this.bySecond = true;
    }
    // A field that filled again keeps no clock: the next time it works out
    // the server sends a fresh countdown.
    for (const id of this.clocks.keys()) {
      if (!live.has(id)) this.clocks.delete(id);
    }
  }

  /** A new world second while a countdown under an hour stands (R2741). */
  outgrown(ctx: ChartContext): boolean {
    return this.bySecond && Math.floor(ctx.worldClockS) !== this.second;
  }

  emit(sinks: ChartSinks, ctx: ChartContext, _frame: FrameInfo): void {
    // The static pack culls nothing: it outlives every pan (R2238).
    const t = ctx.cfg.pieces.spoil_ground;
    const hist = ctx.reading === 'historical';
    const r = t[ctx.reading];
    const face = faceOf(ctx.reading);
    const ink = inkOf(r.ink, CHART_INK.pale);
    const now = Date.now();
    for (const sp of this.spoils) {
      dashedRing(sinks.circles, sp.x, sp.y, t.fig.r, t.fig.width, r.dash, ink, t.fig.alpha);
      const lx = sp.x + t.fig.dx;
      if (hist) {
        ctx.glyphs.word(
          sinks.quads, S.spoil_historical, lx, sp.y, face, t.text.main, ink, r.alpha,
        );
        continue;
      }
      const f = t.modern.fig;
      // The two lines are one block, so they grow about one pivot.
      const pivot = { x: lx, y: sp.y };
      ctx.glyphs.word(
        sinks.quads, S.spoil_modern, lx, sp.y - t.fig.line_h,
        face, t.text.main, ink, r.alpha, { pivot },
      );
      // The countdown runs on the WORLD clock, so a wall second is worth
      // `timescale` of the seconds the server counted in.
      const line = sp.respawnSec <= 0
        ? S.spoil_silts
        : S.spoil_respawn({ short: sp.short, in: leftSpan(leftOf({ sec: sp.respawnSec, atMs: sp.atMs }, now, ctx.state.timescale)) });
      ctx.glyphs.word(
        sinks.quads, line, lx, sp.y + t.fig.line_h,
        face, t.text.sub, inkOf(r.ink, CHART_INK.faint), f.sub_alpha, { pivot },
      );
    }
  }
}

/** The registry's entry. */
export const spoilGround = new SpoilGroundPiece();

/** The world seconds an anchored countdown has left at the wall `now`. */
function leftOf(c: { sec: number; atMs: number }, now: number, timescale: number): number {
  return c.sec - ((now - c.atMs) / 1000) * timescale;
}
